import { useState, useCallback } from 'react';
import NotificationToast from './NotificationToast';
import ConfirmationDialog from './ConfirmationDialog';

/**
 * Custom hook for managing notifications and confirmations
 * Provides functions to show notifications and confirmation dialogs
 * 
 * Usage:
 *   const { showNotification, showConfirmation } = useNotifications();
 *   
 *   // Show a notification
 *   showNotification('Strategy saved!', 'success');
 *   
 *   // Show a confirmation
 *   showConfirmation('Are you sure?', (confirmed) => {
 *     if (confirmed) {
 *       // User confirmed
 *     }
 *   });
 */
export const useNotifications = () => {
  const [notification, setNotification] = useState(null);
  const [confirmation, setConfirmation] = useState(null);

  const showNotification = useCallback((message, type = 'info', duration = 4000) => {
    setNotification({ message, type, duration });
  }, []);

  const showConfirmation = useCallback((message, callback, options = {}) => {
    const {
      confirmText = 'OK',
      cancelText = 'Cancel',
      type = 'warning'
    } = options;

    setConfirmation({
      message,
      confirmText,
      cancelText,
      type,
      onConfirm: () => {
        setConfirmation(null);
        if (callback) callback(true);
      },
      onCancel: () => {
        setConfirmation(null);
        if (callback) callback(false);
      }
    });
  }, []);

  const NotificationComponent = notification ? (
    <NotificationToast
      message={notification.message}
      type={notification.type}
      duration={notification.duration}
      onClose={() => setNotification(null)}
    />
  ) : null;

  const ConfirmationComponent = confirmation ? (
    <ConfirmationDialog
      message={confirmation.message}
      confirmText={confirmation.confirmText}
      cancelText={confirmation.cancelText}
      type={confirmation.type}
      onConfirm={confirmation.onConfirm}
      onCancel={confirmation.onCancel}
    />
  ) : null;

  return {
    showNotification,
    showConfirmation,
    NotificationComponent,
    ConfirmationComponent
  };
};

export default useNotifications;

